import React, { FC, useRef } from "react";
import {
  Box,
  Link,
  Menu,
  Slide,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";

import Logo from "@components/Logo";
import { Colors } from "@theme/colors-twentyfive";

import {
  uiContainerTransitionMs,
  uiOpacityHover,
  useIntersection,
} from "@src/utils";
import Countdown from "../Countdown";
import { Button } from "gatsby-theme-material-ui";
import AiChallengeRegisterButton from "../AiChallengeRegisterButton";
import { useWebsiteStore } from "@src/hooks/useWebsiteStore";
import AiChallengePlayNowButton from "../AiChallengePlayNowButton";
import useAiEscapeRoomOpen from "@src/hooks/useAiEscapeRoomOpen";

interface Props {
  pageContext?: any;
}

const navGradient = `linear-gradient(180deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.00) 100%)`;

const AiChallengeHeader: FC<Props> = ({}) => {
  const openShareModal = useWebsiteStore(
    (state) => state.actions.openModalAiChallengeShare,
  );
  const showPlayNow = useAiEscapeRoomOpen();
  const desktopNav = useMediaQuery("(min-width:1024px)");
  const header = useRef<HTMLElement>();

  const inViewport = useIntersection(header, "0px");

  // state and click handlers for mobile nav
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const NavLogo = (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        gap: "12px",
        justifySelf: "flex-start",
        position: "relative",
      }}
    >
      <Tooltip title="Go to Pangea">
        <Link
          href="/"
          sx={{
            height: "15px",
            color: `${Colors.PrimaryContrast} !important`,
            opacity: 1,
            transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
            "&:hover": {
              opacity: uiOpacityHover,
              color: `${Colors.PrimaryContrast} !important`,
            },
          }}
        >
          <Logo />
        </Link>
      </Tooltip>

      <Link
        href="/landing/ai-escape-room/"
        sx={{
          textDecoration: "none",
        }}
      >
        <Typography
          sx={{
            color: Colors.HeadingText,
            fontWeight: 800,
            fontSize: "15px",
          }}
        >
          Prompt Injection Challenge
        </Typography>
      </Link>
    </Stack>
  );

  const NavItems = (
    <Stack
      sx={{
        justifySelf: "flex-end",
        alignItems: {
          xs: "flex-start",
          md: "center",
        },
        gap: {
          xs: "16px",
          md: "16px",
          lg: "32px",
        },
        Button: {
          fontWeight: 600,
        },
        flexDirection: {
          xs: "column",
          md: "row",
        },
      }}
    >
      <Stack
        sx={{
          gap: "12px",
          width: {
            xs: "100%",
            md: "auto",
          },
          alignItems: {
            xs: "start",
            md: "center",
          },
          flexDirection: {
            xs: "column",
            md: "row",
          },
          "*": {
            width: {
              xs: "100%",
              md: "auto",
            },
          },
        }}
      >
        <Button
          variant="outlined"
          sx={{
            paddingX: {
              md: "16px",
              lg: "32px",
            },
          }}
          onClick={() => {
            openShareModal();
          }}
        >
          Share
        </Button>

        <AiChallengeRegisterButton playNow={showPlayNow} />
      </Stack>
    </Stack>
  );

  const CommonNav = (
    <>
      {NavLogo}

      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate3d(-50%, -50%, 0)",
        }}
      >
        {showPlayNow ? (
          <AiChallengePlayNowButton small={true} sendToRegistration={true} />
        ) : (
          <Countdown small={true} />
        )}
      </Box>

      {NavItems}
    </>
  );

  return (
    <Box
      sx={{
        background: navGradient,
        height: "97px",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Sticky header */}
      {desktopNav && (
        <Slide direction="down" in={!inViewport} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              top: "0",
              width: "100%",
              height: "68px",
              margin: "0 auto",
              zIndex: "5",
              background: navGradient,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                height: "100%",
                maxWidth: "1380px",
                margin: "0 auto",
                padding: { xs: "0 24px", lg: "0 48px" },
                gap: "12px",
                justifyContent: "space-between",
              }}
            >
              {CommonNav}
            </Stack>
          </Box>
        </Slide>
      )}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: `1380px`,
          height: "68px",
          margin: "0 auto",
          zIndex: "5",
        }}
      >
        <Stack
          {...({ ref: header } as any)}
          direction="row"
          alignItems="center"
          sx={{
            height: "100%",
            maxWidth: `1380px`,
            margin: "0 auto",
            padding: { xs: "0 24px", lg: "0 48px" },
            gap: "12px",
            justifyContent: "space-between",
          }}
        >
          {desktopNav ? CommonNav : ""}

          {!desktopNav && (
            <>
              {NavLogo}
              <IconButton onClick={handleClick}>
                <DensityMediumIcon
                  sx={{
                    color: Colors.PrimaryContrast,
                  }}
                />
              </IconButton>
              <Menu
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                sx={{
                  transform: `translate3d(0px, -64px, 0)`,
                  "& .MuiPaper-root": {
                    backgroundColor: Colors.BackgroundBody,
                  },
                  "*": {
                    color: Colors.PrimaryContrast,
                  },
                }}
              >
                <Box
                  sx={{
                    minWidth: "335px",
                    padding: "16px 24px 0",
                    borderRadius: "24px",
                  }}
                >
                  <Stack direction="row" justifyContent="flex-end">
                    <IconButton onClick={handleClose}>
                      <CloseIcon sx={{ color: Colors.HeadingText }} />
                    </IconButton>
                  </Stack>

                  <>{NavItems}</>
                </Box>
              </Menu>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default AiChallengeHeader;
